"use client";

import { useCallback, useEffect } from "react";
import { UserNotification } from "@prisma/client";
import useGetCurrentUser from "@/hooks/useGetCurrentUser";
import useUserNotificationStore from "@/stores/userNotificationStore";
import { useNotificationSSE } from "@/hooks/useNotificationSSE";

/**
 * Global notification manager that:
 * - Connects to SSE for real-time notifications
 * - Updates browser tab title with notification count
 * - Shows native browser notifications
 * - Syncs across tabs via BroadcastChannel
 */
export default function GlobalNotificationManager() {
  const { currentUser } = useGetCurrentUser();
  const { pendingNotifications, refreshPendingNotifications } =
    useUserNotificationStore();

  // Update browser tab title
  useEffect(() => {
    const count = pendingNotifications.length;
    const baseTitle = "Arbogen";

    if (count > 0) {
      document.title = `(${count}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [pendingNotifications.length]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Refresh notifications
  const getUserNotificationsAsync = useCallback(async () => {
    if (!currentUser) {
      return;
    }
    await refreshPendingNotifications();
  }, [currentUser, refreshPendingNotifications]);

  // Handle new notifications from SSE
  const handleNewNotification = useCallback(
    async (notification: UserNotification) => {
      await getUserNotificationsAsync();

      // Show browser notification
      if ("Notification" in window && Notification.permission === "granted") {
        const notif = new Notification("Arbogen", {
          body: notification.message,
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          tag: `notification-${notification.id}`,
          requireInteraction: false,
        });

        // Focus window when notification is clicked
        notif.onclick = () => {
          window.focus();
          notif.close();
        };
      }

      // Broadcast to other tabs
      try {
        const channel = new BroadcastChannel("notifications");
        channel.postMessage({ type: "NEW_NOTIFICATION", notification });
        channel.close();
      } catch (error) {
        console.warn("Failed to broadcast notification:", error);
      }
    },
    [getUserNotificationsAsync],
  );

  // Connect to SSE stream
  const { isConnected } = useNotificationSSE(
    handleNewNotification,
    !!currentUser,
  );

  // Listen to broadcast channel for notifications from other tabs
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") {
      return;
    }

    const channel = new BroadcastChannel("notifications");

    channel.onmessage = (event) => {
      if (event.data.type === "NEW_NOTIFICATION") {
        getUserNotificationsAsync();
      }
    };

    return () => {
      channel.close();
    };
  }, [getUserNotificationsAsync]);

  // Initial load
  useEffect(() => {
    getUserNotificationsAsync();
  }, [getUserNotificationsAsync]);

  // This component doesn't render anything
  return null;
}
